import { useEffect } from "react";
import { AuthClient } from "@anythink-cloud/sdk";

interface UseSessionManagerProps {
  authClient: AuthClient;
  storeHydrated: boolean;
  isPublicAuthPage: boolean;
  pathname: string;
  setLoading: (loading: boolean) => void;
  onValidSession: () => void;
  onInvalidSession: (errorMessage: string) => void;
}

export function useSessionManager({
  authClient,
  storeHydrated,
  isPublicAuthPage,
  pathname,
  setLoading,
  onValidSession,
  onInvalidSession,
}: UseSessionManagerProps) {
  const store = authClient.getStore();
  const session = store((state: any) => state.session);

  useEffect(() => {
    if (!storeHydrated) {
      return;
    }

    // Skip subscription for public auth pages
    if (isPublicAuthPage) {
      setLoading(false);
      return;
    }

    // Get initial session
    const {
      data: { session: initialSession },
    } = authClient.getSession();

    // If we have an initial session, load tenant
    // expires_at is unix timestamp (in seconds since epoch)
    if (
      initialSession?.access_token &&
      (!initialSession.expires_at ||
        Date.now() < initialSession.expires_at * 1000)
    ) {
      onValidSession();
    } else if (
      initialSession?.access_token &&
      initialSession.expires_at &&
      Date.now() >= initialSession.expires_at * 1000
    ) {
      // Session exists but expired; try to refresh
      authClient
        .refreshSession()
        .then(({ data, error }: any) => {
          if (data.session?.access_token && !error) {
            onValidSession();
          } else {
            console.warn(
              "[AuthProvider] Session refresh failed or no access token. Signing out.",
            );
            setLoading(false);
            onInvalidSession("session_expired");
          }
        })
        .catch((error: any) => {
          console.error("[AuthProvider] Initial refresh error:", error);
          setLoading(false);
          onInvalidSession("session_expired");
        });
    } else {
      // No access token at all
      console.warn(
        "[AuthProvider] No access token found. Not authenticated, signing out.",
      );
      setLoading(false);
      onInvalidSession("session_expired");
    }
  }, [
    pathname,
    isPublicAuthPage,
    session,
    storeHydrated,
    authClient,
    onValidSession,
    onInvalidSession,
    setLoading,
  ]);
}
